import { useState, useRef, useEffect, useContext } from 'react';
import './Results.scss';

import PublicImage from 'src/common/components/PublicImage';
import { Button } from 'carbon-components-react';
import { ArrowRight20, Restart20 } from '@carbon/icons-react';
import '@carbon/charts/styles.css';
import { layoutContext } from '../RAIAssessment';
import ResultsType from './ResultsType';
import { Scenario } from '../../data/constants';
import BarChart from './barCharts/Bar';

interface resultsProps {
  contents: any;
}

const Results = (resultsProps: resultsProps) => {
  const { contents } = resultsProps;
  const { results, questions } = contents;
  const { minRecomendation, mediumRecomendation, maxRecomendation } = results;
  const [isFirstSummaryOpen, setisFirstSummaryOpen] = useState(false);
  const [isSecondSummaryOpen, setisSecondSummaryOpen] = useState(false);
  const [isThirdSummaryOpen, setisThirdSummaryOpen] = useState(false);
  const [guidanceStrategy, setGuidanceStrategy] = useState<object[]>([]);
  const [guidanceData, setGuidanceData] = useState<object[]>([]);
  const [guidanceCulture, setGuidanceCulture] = useState<object[]>([]);
  const [graphicData, setGraphicData] = useState<any>([]);
  const resultsRef = useRef<HTMLDivElement>(null);
  const { selectedItems, setQuestionId, setSelectedItems, setAnswers } =
    useContext(layoutContext);
  const { setCurrentScenario } = useContext(layoutContext);
  useEffect(() => {
    const data = [
      {
        score: convert(
          countScore(guidanceStrategy) / countTotalPoints('Strategy', 6),
        ),
      },
      {
        score: convert(countScore(guidanceData) / countTotalPoints('Data', 6)),
      },
      {
        score: convert(
          countScore(guidanceCulture) / countTotalPoints('Culture', 6),
        ),
      },
    ];
    setGraphicData(data);
  }, [guidanceStrategy]);

  useEffect(() => {
    handleGuidance();
  }, []);

  const convert = (num: number) => {
    return Math.round(parseFloat(num.toFixed(2)) * 100);
  };

  const countScore = (selected: any[]) => {
    const score = selected.reduce((acc: number, s: any) => {
      return acc + s.value;
    }, 0);

    return score;
  };

  const countTotalPoints = (questionType: string, questionValue: number) => {
    const count = questions.reduce((acc: number, quesion: any) => {
      return quesion.resultType === questionType ? acc + 1 : acc;
    }, 0);

    return count * questionValue;
  };

  const handleGuidance = () => {
    const arrayStrategy: object[] = [];
    const arrayData: object[] = [];
    const arrayCulture: object[] = [];

    questions.forEach((question: any) => {
      const option: any =
        selectedItems[question.id as keyof typeof selectedItems];
      if (option) {
        const guidanceObj = {
          title: question.title.props.defaultMessage,
          option: option.copy.props.defaultMessage,
          guidance: option.copy.props.guidance,
          resultType: question.resultType,
          value: option.value,
        };
        switch (question.resultType) {
          case 'Strategy':
            arrayStrategy.push(guidanceObj);
            break;
          case 'Data':
            arrayData.push(guidanceObj);
            break;
          case 'Culture':
            arrayCulture.push(guidanceObj);
            break;
        }
      }
    });
    setGuidanceStrategy(arrayStrategy);
    setGuidanceData(arrayData);
    setGuidanceCulture(arrayCulture);
  };

  const handleSeeResults = () => {
    setisFirstSummaryOpen(true);
    setisSecondSummaryOpen(true);
    setisThirdSummaryOpen(true);
  };

  const handleRestartQuiz = () => {
    setQuestionId('q1');
    setSelectedItems({});
    setAnswers({});
    setCurrentScenario(Scenario.introduction);
  };

  return (
    <div
      style={{ margin: 0 }}
      className="rai-assessment__results-page"
      ref={resultsRef}
    >
      <div className="bx--row rai-assessment__results-watsonx">
        <div className="bx--col-lg-6 bx--col-md-16 bx--col-sm-16 rai-assessment__results-wrapper">
          <div className="rai-assessment__results-wrapper-content">
            <h2 className="rai-assessment__results-title">
              {results.resultsTitle}
            </h2>
            <h4 className="rai-assessment__results-description">
              {results.resultsCopy}
            </h4>
            <PublicImage
              imgPath={results.imageSrc}
              widgetId="rai-assessment"
              alt={'alt'}
              className="rai-assessment__results-image"
            />
          </div>
        </div>
        <div className="bx--col-lg-9 bx--col-md-16 bx--col-sm-16 rai-assessment__results-description-wrapper">
          <div className="rai-assessment__results-summary">
            <h4 className="rai-assessment__results-summary-text">
              {results.summaryLabel}
            </h4>
          </div>

          <div className="bx--row">
            <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-16 rai-assessment__results-summary-chart">
              <h4 className="rai-assessment__results-summary-headline">
                {results.graphicCaption}
              </h4>
              <ul>
                <li className="rai-assessment__results-summary-items">
                  <BarChart
                    stats={graphicData.length ? graphicData[0].score : 0}
                    label={results.governanceLabel}
                    minRecomendation={minRecomendation}
                    mediumRecomendation={mediumRecomendation}
                    maxRecomendation={maxRecomendation}
                  />
                </li>
                <li className="rai-assessment__results-summary-items">
                  <BarChart
                    stats={graphicData.length ? graphicData[1].score : 0}
                    label={results.forensicLabel}
                    minRecomendation={minRecomendation}
                    mediumRecomendation={mediumRecomendation}
                    maxRecomendation={maxRecomendation}
                  />
                </li>
                <li className="rai-assessment__results-summary-items">
                  <BarChart
                    stats={graphicData.length ? graphicData[2].score : 0}
                    label={results.cultureLabel}
                    minRecomendation={minRecomendation}
                    mediumRecomendation={mediumRecomendation}
                    maxRecomendation={maxRecomendation}
                  />
                </li>
              </ul>
            </div>
            <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-16">
              <p className="rai-assessment__results-summary-description">
                {results.summaryCopy}
              </p>
              <div
                className="rai-assessment__results-summary-open-results"
                onClick={handleSeeResults}
              >
                <span className="bx--link rai-assessment__results-summary-scroll">
                  {results.summaryScroll}
                </span>
              </div>
            </div>
          </div>
          <div className="bx--row rai-assessment__results-summary-help">
            <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-16">
              <h4 className="rai-assessment__results-summary-headline">
                {results.summaryLinksTitle}
              </h4>
              <ul>
                <li>
                  <p className="rai-assessment__results-bx--link-p">
                    <a
                      className="bx--link"
                      href={`https://www.ibm.com/artificial-intelligence/ai-ethics`}
                      target="_blank"
                    >
                      {results.summaryLink1} →
                    </a>
                  </p>
                </li>
                <li>
                  <p className="rai-assessment__results-bx--link-p">
                    <a
                      className="bx--link"
                      href="https://www.ibm.com/think/videos/ai-ethics-privacy-regulation"
                      target="_blank"
                    >
                      {results.summaryLink2} →
                    </a>
                  </p>
                </li>
                <li>
                  <p className="rai-assessment__results-bx--link-p">
                    <a
                      className="bx--link"
                      href="https://www.ibm.com/consulting/ai-governance"
                      target="_blank"
                    >
                      {results.summaryLink3} →
                    </a>
                  </p>
                </li>
              </ul>
            </div>
            <div className="bx--col-lg-8 bx--col-md-16 bx--col-sm-16">
              <h4 className="rai-assessment__results-summary-headline">
                {results.summarySpeakToExpert}
              </h4>
              <div>
                <a
                  href="https://www.ibm.com/account/reg/us-en/signup?formid=urx-52230"
                  target="_blank"
                >
                  <Button kind="primary" renderIcon={ArrowRight20}>
                    {results.summarySpeakToExpertButton}{' '}
                  </Button>
                </a>
              </div>
            </div>
          </div>
          <div className="bx--row rai-assessment__results-summary-footer">
            <div className="rai-assessment__results-summary-tip-wrapper">
              <details
                className="rai-assessment__results-summary-tip"
                onClick={() => setisFirstSummaryOpen(true)}
                open={isFirstSummaryOpen}
              >
                <summary>
                  {results.governanceLabel}
                  <div className="rai-assessment__results-summary-tip-icon">
                    <ArrowRight20 />
                  </div>
                </summary>
                <div>
                  {guidanceStrategy.map((guidance: any) => (
                    <ResultsType type={guidance} />
                  ))}
                </div>
              </details>

              <details
                className="rai-assessment__results-summary-tip"
                onClick={() => setisSecondSummaryOpen(true)}
                open={isSecondSummaryOpen}
              >
                <summary>
                  {results.forensicLabel}
                  <div className="rai-assessment__results-summary-tip-icon">
                    <ArrowRight20 />
                  </div>
                </summary>
                <div>
                  {guidanceData.map((guidance: any) => (
                    <ResultsType type={guidance} />
                  ))}
                </div>
              </details>
              <details
                className="rai-assessment__results-summary-tip"
                onClick={() => {
                  setisThirdSummaryOpen(isThirdSummaryOpen);
                }}
                open={isThirdSummaryOpen}
              >
                <summary>
                  {results.cultureLabel}
                  <div className="rai-assessment__results-summary-tip-icon">
                    <ArrowRight20 />
                  </div>
                </summary>
                <div>
                  {guidanceCulture.map((guidance: any) => (
                    <ResultsType type={guidance} />
                  ))}
                </div>
              </details>
            </div>

            <div className="rai-assessment__results-summary-cta">
              <Button
                className="rai-assessment__results-summary-cta-restart"
                kind="secondary"
                onClick={handleRestartQuiz}
                renderIcon={Restart20}
              >
                {results.buttonRestart}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Results;
